import { useEffect, useRef } from 'react';

import { useAppConfig } from '#/config/AppConfigContext';
import { useLatest } from '#/hooks/useLatest';
import { useI18n } from '#/i18n/hooks/useI18n';
import { recaptcha } from '#/utils/recaptcha';

import { StyledRecaptchaInput } from './RecaptchaInput.styled';

type Props = {
  onChange: (response?: string) => void;
  siteKey?: string | null;
};

export function RecaptchaInput({ onChange, siteKey }: Props) {
  const { locale } = useI18n();
  const { appConfig } = useAppConfig();

  const ref = useRef<HTMLDivElement | null>(null);

  /**
   * We must use a stable object here to avoid calling the effect multiple times
   */
  const setupRecaptchaRef = useLatest(async (el: HTMLDivElement) => {
    if (!el) return;

    const r = await recaptcha({ locale });
    r.render(el, {
      sitekey: siteKey ?? appConfig.google.recaptcha.siteKey,
      callback: (response: string) => {
        onChange(response);
      },
      'expired-callback': () => {
        // Remove old value because it's no longer valid
        onChange();
      },
    });
  });

  useEffect(() => {
    if (ref.current) void setupRecaptchaRef.current(ref.current);
  }, [setupRecaptchaRef]);

  return <StyledRecaptchaInput ref={ref} />;
}
